import { all, call, takeLatest, put } from 'redux-saga/effects';
import { addToRootSaga } from 'store';
import {
  fetchCampaignLabelsWithExperiments,
  fetchCampaignMediumWithExperiments,
  fetchExperimentConversionsByEventNames,
} from 'api/ExperimentsAPI';

import { Saga } from 'custom-types/saga';
import {
  ConversionObject,
  ExperimentsAction,
  ExperimentsTableFilterOptionPayload,
  ExperimentsTableFilterOptions,
  ExperimentsTableMenuItem,
  LabelObject,
  Member,
  TableFilter,
} from 'modules/experiments/types';
import { addNegativeToast, errorIdentifier } from 'modules/toastContainer';
import { getCommonTranslations } from 'utils/translations';
import * as actions from 'modules/experiments/ducks/actions';
import { ActionTypes } from 'modules/experiments/ducks/actionTypes';
import {
  Bool,
  booleanResponse,
  CONVERSION_EVENT_ID_DELIMITER,
  experimentCampaignTypeDefinition,
  experimentTestSubjects,
  systemConversionEvents,
} from 'modules/experiments/constants';
import { generateSubMenuOptions, getExperimentsTranslations } from 'modules/experiments/utils';
import { fetchOrganizationMembers } from 'api/OrganizationAPI';
import { WhatsApp } from 'constants/messageMediums';

const formatMenuItem = (item: string): ExperimentsTableMenuItem => ({ id: item, labelKey: item });

export function* fetchExperimentsTableFilterOptionsSaga({
  payload,
}: ExperimentsAction<{
  payload: ExperimentsTableFilterOptionPayload;
}>): Saga<ExperimentsAction> {
  try {
    const graphQLQueries = [
      {
        query: fetchCampaignMediumWithExperiments,
        errorMessage: 'errorMessage.campaignMediumFilterOptions',
      },
      {
        query: fetchCampaignLabelsWithExperiments,
        errorMessage: 'errorMessage.campaignLabelsFilterOptions',
      },
      {
        query: fetchExperimentConversionsByEventNames,
        errorMessage: 'errorMessage.customEventFilterOptions',
      },
    ];
    let organizationMembersData = { data: { members: [] } };

    /**
     * We fetch all users in the org to display
     * the options for the 'Modified By' filter
     */
    try {
      organizationMembersData = yield call(fetchOrganizationMembers);
    } catch (e) {
      /**
       * This call is going to fail for non admin users. Till we have a new endpoint.
       * We will not be showing the 'Modified By' filter to non admin users.
       */
    }

    /**
     * Collecting all filter options in parallel.
     * By making remote API calls
     */
    const [campaignMediumData, campaignLabelData, experimentConversionsByEventNamesData] =
      yield all(
        graphQLQueries.map(({ query, errorMessage }) =>
          call(function* queryOptionData(): any {
            let response = { data: {} };
            // Even if one of these calls is to fail this would prevent
            // the other calls from being short-circuited.
            try {
              response = yield call(query);
            } catch (e) {
              // Notify user if there was an error fetching some filter options.
              const filterOptErrorMessage = yield call(getExperimentsTranslations, errorMessage);
              yield put(
                addNegativeToast(
                  filterOptErrorMessage,
                  errorIdentifier({
                    errorMessage: filterOptErrorMessage,
                    source: 'fetchExperimentsTableFilterOptionsSaga',
                    error: e,
                  }),
                ),
              );
            }
            return response;
          }),
        ),
      );

    /**
     * Collecting the corresponding translated texts for filter options
     * By making in-memory calls to the translation namespaces.
     */
    const [
      testSubjectsLabels,
      systemConversionEventsLabels,
      booleanResponseLabels,
      testStrategyLabels,
    ] = yield all([
      call(getExperimentsTranslations, 'experimentType', {
        returnObjects: true,
      }),
      call(getExperimentsTranslations, 'filters.experiments.systemConversionEvents', {
        returnObjects: true,
      }),
      call(getExperimentsTranslations, 'filters.experiments.booleanResponse', {
        returnObjects: true,
      }),
      call(getExperimentsTranslations, 'testStrategy', {
        returnObjects: true,
      }),
    ]);

    const [campaignTypeLabels, messageMediumsLabels] = yield all([
      call(getCommonTranslations, 'labels.campaignTypes', {
        returnObjects: true,
      }),
      call(getCommonTranslations, 'labels.mediums', {
        returnObjects: true,
      }),
    ]);

    const [translationFilterHeaders, translationFilterCampaigns, translationFilterExperiment] =
      yield all([
        call(getExperimentsTranslations, 'filters.headers', {
          returnObjects: true,
        }),
        call(getExperimentsTranslations, 'filters.campaigns', {
          returnObjects: true,
        }),
        call(getExperimentsTranslations, 'filters.experiments.mainMenu', {
          returnObjects: true,
        }),
      ]);

    const {
      data: { members = [] },
    } = organizationMembersData;

    const {
      data: { conversionsByEventNames = [] },
    } = experimentConversionsByEventNamesData;

    const {
      data: { campaignLabels = [] },
    } = campaignLabelData;

    const {
      data: { messageMediums = [] },
    } = campaignMediumData;

    const options: ExperimentsTableFilterOptions = {
      [TableFilter.EXPERIMENT_MODIFIED_BY]: generateSubMenuOptions({
        parentMenuId: TableFilter.EXPERIMENT_MODIFIED_BY,
        menuItems: (members as Member[])
          .filter(({ projectMemberships }) =>
            projectMemberships.map(({ projectId }) => projectId).includes(payload.projectId),
          )
          .map(({ email, firstName, lastName }) => ({
            id: email,
            labelKey: `${firstName} ${lastName}`,
          })),
      }),
      [TableFilter.EXPERIMENT_TEST_SUBJECTS]: generateSubMenuOptions({
        parentMenuId: TableFilter.EXPERIMENT_TEST_SUBJECTS,
        translationObject: testSubjectsLabels,
        menuItems: experimentTestSubjects.map(formatMenuItem),
      }),
      [TableFilter.EXPERIMENT_WINNING_CRITERIA]: generateSubMenuOptions({
        parentMenuId: TableFilter.EXPERIMENT_WINNING_CRITERIA,
        translationObject: systemConversionEventsLabels,
        menuItems: [
          { id: 'WinningCriteria-SystemEvent', labelKey: 'SystemEvents', isTitle: true },
          ...systemConversionEvents.map(formatMenuItem),
          { id: 'WinningCriteria-CustomEvent', labelKey: 'CustomEvents', isTitle: true },
          ...conversionsByEventNames.map(({ eventName, conversions }: ConversionObject) => ({
            id: conversions.map(({ id }) => id).join(CONVERSION_EVENT_ID_DELIMITER),
            labelKey: eventName,
          })),
        ],
      }),
      [TableFilter.EXPERIMENT_WINNER_SELECTED]: generateSubMenuOptions({
        parentMenuId: TableFilter.EXPERIMENT_WINNER_SELECTED,
        translationObject: booleanResponseLabels,
        menuItems: booleanResponse.map(formatMenuItem),
      }),
      [TableFilter.EXPERIMENT_SPLIT_VARIATIONS_RANDOMLY]: generateSubMenuOptions({
        parentMenuId: TableFilter.EXPERIMENT_SPLIT_VARIATIONS_RANDOMLY,
        translationObject: testStrategyLabels,
        menuItems: [
          { id: Bool.TRUE, labelKey: 'allUsers' },
          { id: Bool.FALSE, labelKey: 'subset' },
        ],
      }),
      [TableFilter.CAMPAIGN_LABELS]: generateSubMenuOptions({
        parentMenuId: TableFilter.CAMPAIGN_LABELS,
        menuItems: campaignLabels.map(({ id, name }: LabelObject) => ({
          id: id.toString(),
          labelKey: name,
        })),
      }),
      [TableFilter.CAMPAIGN_MEDIUM]: generateSubMenuOptions({
        parentMenuId: TableFilter.CAMPAIGN_MEDIUM,
        translationObject: messageMediumsLabels,
        menuItems: messageMediums
          .filter((med: string) => !!payload.isWhatsAppFFOn || med !== WhatsApp)
          .map(formatMenuItem),
      }),
      [TableFilter.CAMPAIGN_TYPE]: generateSubMenuOptions({
        parentMenuId: TableFilter.CAMPAIGN_TYPE,
        translationObject: campaignTypeLabels,
        menuItems: experimentCampaignTypeDefinition.map(formatMenuItem),
      }),
    };

    yield put(
      actions.fetchExperimentsTableFilterOptionResponse({
        options,
        translations: {
          ...translationFilterHeaders,
          ...translationFilterCampaigns,
          ...translationFilterExperiment,
        },
      }),
    );
  } catch (error) {
    const errorMessage = yield call(
      getExperimentsTranslations,
      'errorMessage.fetchExperimentsTableFilterOptions',
    );
    yield put(actions.fetchExperimentsTableFilterOptionError());
    yield put(
      addNegativeToast(
        errorMessage,
        errorIdentifier({
          errorMessage,
          source: 'fetchExperimentsTableFilterOptionsSaga',
          error,
        }),
      ),
    );
  }
}

addToRootSaga([
  takeLatest(ActionTypes.FETCH_TABLE_FILTER_OPTION_REQUEST, fetchExperimentsTableFilterOptionsSaga),
]);
